import { ChevronDown, ChevronRight, CircleAlert, Copy, LoaderCircle, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import { clock, dayHeader } from '../lib/format';
import { readParams, withParam, writeUrl } from '../lib/urlState';
import { useLoad } from '../lib/useLoad';

interface AuditCategory {
  id: string;
  label: string;
}

/** The two selects, and the reason there is nothing to select if there is not. */
interface AuditFilters {
  tenants: string[];
  categories: AuditCategory[];
  unavailable?: string;
}

interface AuditPage {
  entries: AuditEntry[];
  nextCursor: string | null;
  unreadable: { tenant: string; reason: string }[];
  unavailable?: string;
  note: string;
}

interface AuditEntry {
  id: number;
  tenantKey: string;
  category: string;
  /** The filter's label for this row's category, whichever spelling it was stored under. */
  categoryLabel: string;
  subCategory: string | null;
  api: string | null;
  categoryId: number | null;
  errorMsg: string | null;
  errorJson: Record<string, unknown> | null;
  jsonBlock: Record<string, unknown> | null;
  redacted: boolean;
  createdAt: string;
  utcTime: string | null;
}

/**
 * dispatch-be's audit trail, laid out the way the dispatch dashboard lays it
 * out — date groups, a one-line card per entry, click to expand into the
 * payload — because operators already read that screen and a second, different
 * arrangement of the same records is a thing to relearn under pressure.
 *
 * ONE DELIBERATE DIFFERENCE. The dashboard renders `json_block` raw. This does
 * not: dispatch-be's SignIn writes its request body before checking credentials,
 * so that column holds a plaintext password for every login attempt (KAN-576),
 * and a pg error object can carry bound query parameters. Credential-shaped
 * fields are replaced server-side and the row says when that happened — an
 * omitted key would read as "the request did not have one", which is a
 * different and misleading fact.
 *
 * The payload is otherwise complete, because a redaction that hides the data
 * people need is a redaction people work around by reading the raw table.
 */

const PAGE = 50;

export function Audit() {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // The question is the URL's, so a link can open this screen on a tenant --
  // the incident drawer's "audit trail" does -- and a search survives a reload.
  const [tenant, setTenant] = useState(() => readParams().get('tenant') ?? '');
  const [category, setCategory] = useState(() => readParams().get('category') ?? '');
  const [search, setSearch] = useState(() => readParams().get('q') ?? '');
  const [applied, setApplied] = useState(() => readParams().get('q') ?? '');
  const [errorsOnly, setErrorsOnly] = useState(() => readParams().get('errors') === 'true');
  const [copied, setCopied] = useState<string | null>(null);
  // Pages already loaded, kept separately from the first page so "Load more"
  // appends rather than replacing — and so the 30s poll refreshes only the head.
  const [more, setMore] = useState<AuditEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  // Read by the poll, which must not move the first page while older pages
  // hang off it -- see the head's loader below.
  const paging = useRef(false);
  paging.current = more.length > 0;
  // The first page, WITH the question it answers: a changed filter renders
  // once with the old pages still loaded, and must not be answered with them.
  const head = useRef<{ query: string; page: AuditPage } | null>(null);

  useEffect(() => {
    let params = withParam(readParams(), 'tenant', tenant || null);
    params = withParam(params, 'category', category || null);
    params = withParam(params, 'q', applied || null);
    params = withParam(params, 'errors', errorsOnly ? 'true' : null);
    writeUrl(params);
  }, [tenant, category, applied, errorsOnly]);

  // The requests still out: the page for the question on screen, and a "Load
  // more". A newer question, or leaving the screen, aborts them — and the
  // server cancels the statement behind an aborted one. That matters because
  // audit reads hold ONE dispatch connection between them, whoever is asking
  // (the checks need the rest): a search nobody is waiting for any more would
  // hold it while the one somebody is waiting for queued behind it, or was
  // told to try again.
  const pageFlight = useRef<AbortController | null>(null);
  const moreFlight = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      pageFlight.current?.abort();
      moreFlight.current?.abort();
    },
    [],
  );

  // All this screen loads on open: one query against sphere-watch's own tenant
  // mirror, plus a constant category list. No audit row is read yet.
  const { data: filters, error: filtersError, loading: filtersLoading } = useLoad(
    () => api.get<AuditFilters>('/audit/filters'),
    [],
  );

  // NOTHING IS FETCHED UNTIL BOTH SELECTS ARE SET, and that is what the two
  // steps are for. `audit_logs` is one table per tenant schema, so an unfiltered
  // page was a separate query per monitored schema — up to 25, run one after the
  // other against the dispatch database and merged here — before a single row
  // could render. Opening the screen used to pay all of that for a page of
  // "everything, everywhere" that the operator then filtered away anyway.
  // Tenant narrows the read to one schema and category to one category, so the
  // first page fetched is the first page somebody actually asked for. The
  // server now refuses /audit without both (400, with the reason, which the
  // banner below shows), so this gate is what keeps the screen from asking.
  const ready = Boolean(tenant && category);

  const query = useMemo(() => {
    const params = new URLSearchParams({ limit: String(PAGE) });
    if (tenant) params.set('tenant', tenant);
    if (category) params.set('category', category);
    if (applied) params.set('search', applied);
    if (errorsOnly) params.set('errors', 'true');
    return params.toString();
  }, [tenant, category, applied, errorsOnly]);

  // Any filter change discards the accumulated pages: keeping them would mix
  // rows that match the new filter with rows that matched the old one.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new question is what discards the loaded pages; the body never reads it
  useEffect(() => {
    moreFlight.current?.abort();
    setMore([]);
    setCursor(null);
    setMoreError(null);
  }, [query]);

  const { data, error, stale, reload } = useLoad<AuditPage | null>(
    () => {
      // WHILE OLDER PAGES ARE LOADED THE FIRST PAGE STAYS PUT. They were read
      // from a cursor at the end of THAT first page. Refreshing it moved its
      // end forward as new rows arrived, so the rows between its new end and
      // the loaded pages fell out of both -- an audit page silently skipping
      // records, the one failure paging here exists to prevent -- and the next
      // "Load more" took the refreshed page's cursor and fetched page two
      // again, every row twice. So the poll answers with what it already has
      // until "Back to the newest" lets go of the older pages.
      if (paging.current && head.current?.query === query) return Promise.resolve(head.current.page);
      // A poll never gets here with a request out (useLoad drops that tick),
      // so whatever this aborts is the previous question's.
      pageFlight.current?.abort();
      if (!ready) return Promise.resolve(null);
      const controller = new AbortController();
      pageFlight.current = controller;
      return api.get<AuditPage>(`/audit?${query}`, { signal: controller.signal }).then((page) => {
        head.current = { query, page };
        return page;
      });
    },
    [query, ready],
    // Polling an unasked-for query would put the fan-out back, every 30s.
    ready ? 30_000 : undefined,
  );

  // A changed search or filter is in flight, and the rows on screen are still
  // the previous answer. Not the 30s poll: that re-reads the same question, and
  // a spinner that turned on by itself every half-minute would mean nothing.
  const searching = ready && stale;

  const submit = () => setApplied(search.trim());

  useEffect(() => {
    if (data?.nextCursor !== undefined) setCursor(data.nextCursor);
  }, [data]);

  /** Let go of the older pages and read the first one fresh, polling again. */
  const backToNewest = () => {
    moreFlight.current?.abort();
    paging.current = false;
    setMore([]);
    setMoreError(null);
    void reload();
  };

  const loadMore = async () => {
    if (!cursor) return;
    const controller = new AbortController();
    moreFlight.current = controller;
    setLoadingMore(true);
    setMoreError(null);
    try {
      const next = await api.get<AuditPage>(`/audit?${query}&cursor=${encodeURIComponent(cursor)}`, {
        signal: controller.signal,
      });
      // De-duplicated as well, by the key the rows are drawn under: a row
      // twice is two rows that expand together and a count that lies.
      setMore((current) => {
        const seen = new Set([...(data?.entries ?? []), ...current].map((row) => `${row.tenantKey}:${row.id}`));
        return [...current, ...next.entries.filter((row) => !seen.has(`${row.tenantKey}:${row.id}`))];
      });
      setCursor(next.nextCursor);
    } catch (caught) {
      // Said beside the button: it used to fail with nothing on screen, and a
      // "busy, try again in 4s" is now an answer it can get. Not when it was
      // aborted — the question changed, and nobody wants that page.
      if (!controller.signal.aborted) setMoreError((caught as Error).message);
    } finally {
      setLoadingMore(false);
    }
  };

  // Filtering is done SERVER-side so pagination stays correct: filtering the
  // loaded page in the browser would page through unfiltered rows and show a
  // handful of matches per "page" with no way to reach the rest.
  const entries = useMemo(() => [...(data?.entries ?? []), ...more], [data, more]);

  const groups = useMemo(() => {
    const out: [string, AuditEntry[]][] = [];
    for (const entry of entries) {
      const header = dayHeader(entry.createdAt);
      const last = out[out.length - 1];
      if (last && last[0] === header) last[1].push(entry);
      else out.push([header, [entry]]);
    }
    return out;
  }, [entries]);

  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const copy = async (key: string, text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1600);
  };

  if (filtersLoading) return <div className="wt-empty">Loading…</div>;
  if (filtersError) return <div className="wt-banner wt-banner-danger">{filtersError.message}</div>;
  if (filters?.unavailable) {
    return (
      <div className="wt-empty">
        <div className="big">Audit is not readable yet.</div>
        <div>{filters.unavailable}</div>
      </div>
    );
  }

  // Which of the two steps is still outstanding. Said in the body rather than
  // left as an empty list, because a screen that opens blank reads as broken.
  const gate = (
    <div className="wt-empty">
      <div className="big">{tenant ? 'Now pick a category.' : 'Pick a tenant to start.'}</div>
      <div className="hint">
        {tenant
          ? `${tenant}'s entries load as soon as a category is chosen.`
          : 'The trail is a separate table per tenant, so this screen reads one tenant and one category at a time — that is what keeps it quick.'}
      </div>
    </div>
  );

  // Dimmed, not blanked, while a new search is out: blanking would make the
  // list jump twice per search, and the old rows are a fair placeholder.
  const list = (
    <div className={searching ? 'wt-audit-list is-searching' : 'wt-audit-list'} aria-busy={searching}>
      {data?.unreadable?.length ? (
        <div className="wt-banner wt-banner-warn">
          {data.unreadable.length} tenant schema(s) could not be read — usually a missing GRANT on
          audit_logs. Their activity is <strong>absent from this list, not empty</strong>.
        </div>
      ) : null}

      {groups.map(([header, rows]) => (
        <section key={header}>
          <div className="wt-daygroup">{header}</div>
          {rows.map((entry) => {
            const key = `${entry.tenantKey}-${entry.id}`;
            const open = expanded.has(key);
            const failed = Boolean(entry.errorMsg);
            const payload = JSON.stringify(entry.jsonBlock ?? {}, null, 2);
            return (
              <article key={key} className={`wt-audit ${failed ? 'failed' : ''}`}>
                <button type="button" className="wt-audit-row" onClick={() => toggle(key)}>
                  {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <strong>
                    #{entry.categoryId ?? '—'} · {entry.categoryLabel}
                  </strong>
                  {entry.subCategory && <span className="wt-chip">{entry.subCategory}</span>}
                  <span className="wt-chip">{entry.tenantKey}</span>
                  <span className="wt-audit-api">{entry.api}</span>
                  {failed && <CircleAlert size={14} style={{ color: 'var(--danger)' }} />}
                  <span className="wt-when">{clock(entry.createdAt)}</span>
                </button>

                {open && (
                  <div className="wt-audit-detail">
                    <div className="wt-audit-meta">
                      <span>
                        <b>Log ID</b> {entry.id}
                      </span>
                      <span>
                        <b>Status</b>{' '}
                        <span style={{ color: failed ? 'var(--danger)' : 'var(--ok)' }}>
                          {failed ? 'Failed' : 'OK'}
                        </span>
                      </span>
                      <span>
                        <b>UTC</b> {entry.utcTime ? new Date(entry.utcTime).toISOString() : '—'}
                      </span>
                      <span>
                        <b>Route</b> <code>{entry.api}</code>
                      </span>
                    </div>

                    {failed && (
                      <div className="wt-audit-error">
                        <div className="title">
                          <CircleAlert size={14} /> Error details
                        </div>
                        <div>{entry.errorMsg}</div>
                        {entry.errorJson && (
                          <pre>{JSON.stringify(entry.errorJson, null, 2)}</pre>
                        )}
                      </div>
                    )}

                    <div className="wt-audit-payload-head">
                      <span>Payload data (JSON)</span>
                      {entry.redacted && (
                        <span className="wt-chip wt-chip-flap">
                          credential fields replaced — see KAN-576
                        </span>
                      )}
                      <span className="wt-spacer" />
                      <button type="button" className="btn btn-ghost" onClick={() => void copy(key, payload)}>
                        <Copy size={13} /> {copied === key ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                    <pre className="wt-audit-payload">{payload}</pre>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      ))}

      {moreError && <div className="wt-banner wt-banner-danger">{moreError}</div>}

      {cursor && entries.length > 0 && (
        <div className="wt-more">
          <button type="button" className="btn btn-secondary" disabled={loadingMore} onClick={() => void loadMore()}>
            {loadingMore ? 'Loading…' : `Load ${PAGE} more`}
          </button>
          <span className="wt-when">{entries.length} loaded, newest first</span>
          {more.length > 0 && (
            <>
              <span className="wt-when">· not refreshing while you page back</span>
              <button type="button" className="btn btn-ghost" onClick={backToNewest}>
                Back to the newest
              </button>
            </>
          )}
        </div>
      )}

      {/* Not while searching: the cursor is reset the moment the filter
          changes, so this would vouch for the OLD rows being all the new
          filter has. */}
      {!cursor && !searching && entries.length > 0 && (
        <div className="wt-more">
          <span className="wt-when">{entries.length} entries — that is all of them for this filter.</span>
        </div>
      )}

      {!entries.length && (
        <div className="wt-empty">
          <div className="big">Nothing matches.</div>
          <div>Try a different search, or another category.</div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className="wt-head">
        <h1>Audit</h1>
        <span className="sub">
          {ready
            ? `${entries.length} entries · ${entries.filter((e) => e.errorMsg).length} failed`
            : 'Pick a tenant, then a category'}
        </span>
      </div>

      <div className="wt-filters">
        <select
          id="audit-tenant"
          aria-label="Tenant"
          className="select"
          value={tenant}
          onChange={(event) => setTenant(event.target.value)}
        >
          <option value="">Select tenant…</option>
          {(filters?.tenants ?? []).map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        {/* Second, and disabled until the first is answered: the pair is what
            keeps a page to one schema and one category. */}
        <select
          id="audit-category"
          aria-label="Category"
          className="select"
          value={category}
          disabled={!tenant}
          onChange={(event) => setCategory(event.target.value)}
        >
          <option value="">{tenant ? 'Select category…' : 'Tenant first…'}</option>
          {(filters?.categories ?? []).map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>

        <label className="wt-search">
          <Search size={14} />
          <input
            className="input"
            placeholder="route, method, error, or id…"
            value={search}
            disabled={!ready}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
            onBlur={submit}
          />
        </label>

        {/* The spinner takes the magnifier's place rather than joining it, so
            the button keeps its width and the row beside it does not shift. */}
        <button type="button" className="btn btn-secondary" disabled={!ready} aria-busy={searching} onClick={submit}>
          {searching ? <LoaderCircle size={14} className="wt-spin" /> : <Search size={14} />}
          Search
        </button>

        <button
          type="button"
          className={errorsOnly ? 'btn btn-primary' : 'btn btn-secondary'}
          disabled={!ready}
          onClick={() => setErrorsOnly((value) => !value)}
        >
          Failures only
        </button>

        {(tenant || category || applied || errorsOnly) && (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setTenant('');
              setCategory('');
              setSearch('');
              setApplied('');
              setErrorsOnly(false);
            }}
          >
            Clear
          </button>
        )}
      </div>

      {!ready ? (
        gate
      ) : error ? (
        <div className="wt-banner wt-banner-danger">{error.message}</div>
      ) : !data ? (
        // `useLoad` reports `loading` only before its FIRST run, and the first
        // run here is the no-op the gate short-circuits — so "asked, nothing
        // back yet" is a null page, not `loading`. Reading it off `loading`
        // flashes "Nothing matches" over a query that is still in flight.
        <div className="wt-empty">Loading…</div>
      ) : data.unavailable ? (
        // The mirror can go cold after the filter list came back, and an empty
        // list would read as "this tenant did nothing" rather than "we could not
        // look".
        <div className="wt-empty">
          <div className="big">Audit is not readable yet.</div>
          <div>{data.unavailable}</div>
        </div>
      ) : (
        list
      )}
    </>
  );
}
